import { useRef, useEffect, useState } from 'react';
import { Outlet, useRoutes, useLocation } from 'react-router-dom';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import isEqual from 'lodash.isequal';

import { usePrevious, PermissionEnum } from '@ui/src';
import { routes as accountsRoutes } from '@accounts/routes-content';
import { setAccountsSettings } from '@accounts/store/dictionaries/slice';
import {
  useLazyGetAccountsListQuery,
  useLazyGetAccountsStatsQuery,
} from '@accounts/hoc/accounts/store/services';
import { routes as authRoutes } from '@auth/routes-content';
import { routes as customerRoutes } from '@clients/routes-content';
import ClientsRoutes from '@clients/routes';
import {
  setActivePermissions as setClientsActivePermissions,
  setPaymentTypes as setClientsPaymentTypes,
  setCustomersBaseCurrency,
  setCustomersCountries,
  setCustomersCurrencies,
  setCustomersSettings,
} from '@clients/store/dictionaries/slice';
import { setRateSources as setCustomersRateSource } from '@clients/hoc/customers/store/slices/tariffs/tariff';
import { useLazyGetIndividualUserQuery } from '@clients/hoc/individual/store/services/individual';
import { useLazyGetCorporateUserQuery } from '@clients/hoc/corporate/store/services/corporate';
import { setNotificationsItems as setCustomersNotificationsItems } from '@clients/hoc/notification-center/store/slices';
import { routes as configurationRoutes } from '@configuration/routes-content';
import {
  setActivePermissions as setConfigurationActivePermissions,
  setPaymentTypes as setConfigurationPaymentTypes,
  setConfigurationBaseCurrency,
  setConfigurationCountries,
  setConfigurationCurrencies,
  setConfigurationSettings,
} from '@configuration/store/dictionaries/slice';
import { useLazyGetConfigurationPspAccountsQuery } from '@configuration/hoc/bank-and-psp/store/services';
import { routes as exchangeRoutes } from '@exchange/routes-content';
import {
  setActivePermissions as setCurrensiesExchangeActivePermissions,
  setCurrenciesExchangeCurrencies,
  setCurrenciesExchangeRateSources,
} from '@exchange/store/dictionaries/slices';
import { setActivePermissions as setDashboardActivePermissions } from '@dashboard/store/dictionaries/slice';

import { routes as dashboardRoutes } from '@dashboard/routes-content';
import { routes as administrationRoutes } from '@administration/routes-content';
import { setPaymentTypes as setEmployeesPaymentTypes } from '@administration/hoc/settings/store/slices/payment-types';
import {
  setActivePermissions as setEmployeesActivePermissions,
  setAdministrationCountries,
  setAdministrationCurrencies,
  setCardsEnabled,
} from '@administration/store/dictionaries/slice';
import {
  setAdministationPspDetails,
  setAdministationRateSources,
} from '@administration/hoc/settings/store/slices/details';
import { PSPDetailsKeys, RateSource } from '@administration/hoc/settings/types';
import { useLazyGetUsersQuery } from '@administration/hoc/user-list/store/services/users';
import { setBrowserNotificationEnabled } from '@administration/hoc/settings/profile/store/slices';
import { useLazyGetRolesQuery } from '@administration/hoc/user-roles/store/services/roles';
import { routes as messagesRoutes } from '@messaging/routes-content';
import { setActivePermissions as setMessagingActivePermissions } from '@messaging/store/dictionaries/slices';
import {
  setMainChannels,
  setRequestChannels,
  setNewMessageCount,
} from '@messaging/hoc/store/slices/general';
import { setMessages, setDocuments } from '@messaging/hoc/store/slices/message';
import { Message, MessageType, Document } from '@messaging/hoc/types';
import { useLazyGetMessageDocumentQuery } from '@messaging/hoc/store/services';
import { routes as notificationCenterRoutes } from '@notifications/routes-content';
import NotificationCenterRoutes from '@notifications/routes';
import { setActivePermissions as setNotificationCenterPermissions } from '@notifications/store/dictionaries/slices';
import { setNotificationsItems } from '@notifications/hoc/center/store/slices';
import {
  NotificationCategoryEnum,
  NotificationItem,
} from '@notifications/hoc/center/types';
import { routes as reportsRoutes } from '@reports/routes-content';
import {
  setActivePermissions as setReportsActivePermissions,
  setReportsBaseCurrency,
  setReportsCurrencies,
} from '@reports/store/dictionaries/slice';
import { setReportsSettings } from '@reports/hoc/profit-loss/store/slices/frs';
import { routes as amlKycRoutes } from '@aml/routes-content';
import {
  setActivePermissions as setAmlActivePermissions,
  setAmlBaseCurrency,
  setAmlCurrencies,
  setPaymentTypes as setAMLPaymentTypes,
} from '@aml/store/dictionaries/slices';
import { routes as transactionsRoutes } from '@transactions/routes-content';
import TransactionsRoutes from '@transactions/routes';

import {
  setActivePermissions as setTransactionsActivePermissions,
  setPaymentTypes as setTransactionsPaymentTypes,
  setTransactionsBaseCurrency,
  setTransactionsCountries,
  setTransactionsCurrencies,
  setTransactionsRateSources,
  setTransactionsSettings,
} from '@transactions/store/dictionaries/slice';
import { setNotificationsItems as setTransactionsNotificationsItems } from '@transactions/hoc/notification-center/store/slices';
import { setFintracSettings } from '@reports/hoc/fintrac/store/slices/list';
import { useLazyGetEmployeesTransactionsTypesQuery } from '@administration/hoc/settings/store/services/transactions-types';
import { useAppSelector, useAppDispatch } from '@store';
import {
  useGetRootMessageTokenMutation,
  useGetWhitelabelQuery,
  useLazyGetPaymentTypesQuery,
  useLazyGetRootDictionariesQuery,
  useLazyGetFFSettingsQuery,
} from './store/service';
import {
  setNotificationMessages,
  setPaymentTypes as setRootPaymentTypes,
  setRootSettings,
  setNotificationVisible,
} from './store/slice';
import { NotificationMessage, PSPDetails } from './store/types';
import setSocketMessageNotificationVisible from './hooks/socket-notification-helper';

import Layout from './components/layout/layout';

import usePrivateRoutes from './hooks/use-private-routes';
import useIsAuthenticated from './hooks/use-is-authenticated';

const RootRoutes = () => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [notificationPanelVisible, setNotificationPanelVisible] =
    useState(false);
  const [selectedNotificationId, setSelectedNotificationId] = useState('');

  const {
    activePermissions,
    accessToken,
    notificationsMessages,
    notificationChannelId,
    whitelabel,
    paymentTypes,
    userId: userIdStore,
    countries,
    currencies,
    baseCurrency,
    settings,
    logo,
    notificationVisible,
  } = useAppSelector((state) => state.root);
  const {
    general: { mainChannels, requestChannels, scrolled, newMessageCount },
    message: { messages, documents },
  } = useAppSelector((state) => state.messages);
  const { roles } = useAppSelector((state) => state.userRoles.roles);
  const { users } = useAppSelector((state) => state.userList.users);
  const { notifications: customersNotifications } = useAppSelector(
    (state) => state.customersNotificationsCenter,
  );
  const {
    paymentTypes: { paymentTypes: settingsPaymentTypes, isSuccess },
    details: { rateSources: settingsRateSource, pspDetails },
    profile: { browserNotificationEnabled },
  } = useAppSelector((state) => state.settings);
  const { rateSource: exchangeRateSource } = useAppSelector(
    (state) => state.exchangeDictionaries,
  );
  const { rateSources: transactionsRateSource } = useAppSelector(
    (state) => state.transactionsDictionaries,
  );
  const { rateSources: customersRateSource } = useAppSelector(
    (state) => state.customerTariffs,
  );
  const { notifications } = useAppSelector((state) => state.notificationCenter);
  const { notifications: transactionsNotifications } = useAppSelector(
    (state) => state.transactionsNotificationCenter,
  );
  const { accountsTakeCount, bankId } = useAppSelector(
    (state) => state.bankAndPsp.info,
  );

  const prevPaymentTypes = usePrevious(settingsPaymentTypes);

  const { pathname, search } = useLocation();

  const [getIndividualUser] = useLazyGetIndividualUserQuery();
  const [getCorporateUser] = useLazyGetCorporateUserQuery();
  const [getAccountsList] = useLazyGetAccountsListQuery();
  const [getPspAccountsList] = useLazyGetConfigurationPspAccountsQuery();
  const [getAccountsStats] = useLazyGetAccountsStatsQuery();
  const [getRoles] = useLazyGetRolesQuery();
  const [getFFSettings] = useLazyGetFFSettingsQuery();

  const [getUsers] = useLazyGetUsersQuery();

  const dispatch = useAppDispatch();

  const [getMessageDocument] = useLazyGetMessageDocumentQuery();
  const [getMessagesToken] = useGetRootMessageTokenMutation();

  useGetWhitelabelQuery();
  const [getPaymentTypes] = useLazyGetPaymentTypesQuery();
  const [getRootDitionaries] = useLazyGetRootDictionariesQuery();
  const [getTransactionTypes] = useLazyGetEmployeesTransactionsTypesQuery();

  const prevAccessToken = usePrevious(accessToken);

  const getMessageDocumentAction = async (
    channelId: string,
    documentId: string,
  ) => {
    const response = await getMessageDocument({ channelId, documentId });

    return response.data;
  };

  const notify = ({ text, id }: { text: string; id: string }) => {
    if (!('Notification' in window)) {
      // eslint-disable-next-line no-alert
      alert('This browser does not support desktop notification');
    } else if (Notification.permission === 'granted') {
      const notification = new Notification(
        `New notification in ${whitelabel?.data?.value?.companyName as string}`,
        {
          icon: logo,
          body: text,
        },
      );

      notification.onclick = () => {
        window.parent.parent.focus();

        setTimeout(() => {
          setNotificationPanelVisible(true);
          setSelectedNotificationId(id);
        }, 10);
      };
    } else if (Notification.permission !== 'denied') {
      Notification.requestPermission().then((permission) => {
        if (permission === 'granted') {
          const notification = new Notification(
            `New notification in ${
              whitelabel?.data?.value?.companyName as string
            }`,
            {
              icon: logo,
              body: text,
            },
          );

          notification.onclick = () => {
            window.parent.parent.focus();

            setTimeout(() => {
              setNotificationPanelVisible(true);
              setSelectedNotificationId(id);
            }, 10);
          };
        }
      });
    }
  };

  const { readyState, sendMessage } = useWebSocket(
    `${process.env.API_WSS_HOST}?token=${accessToken}`,
    {
      share: true,
      shouldReconnect: () => false,
      onOpen: () => {},
      onClose: (event) => {
        if (event.code !== 1005 && event.code !== 1006) {
          getMessagesToken();
        }
      },
      onMessage: async (event) => {
        const parsed = JSON.parse(event.data);

        if (parsed?.event === 'message' || parsed?.event === 'edit-message') {
          const messageData = parsed?.data;

          if (messageData.channelId === notificationChannelId) {
            let messageContent;
            let title;

            if (messageData.content) {
              if (typeof messageData.content === 'string') {
                messageContent = JSON.parse(messageData.content);
              } else {
                messageContent = messageData.content;
              }
            }

            if (messageContent) {
              if (messageData.eventType === 'NOTIFICATION') {
                const titleArray = messageContent.notification.type
                  ?.split('_')
                  .map(
                    (type: string) =>
                      `${type.charAt(0)}${type.slice(1).toLowerCase()}`,
                  );

                title = `${titleArray[0]} - ${titleArray.slice(1).join(' ')}`;

                if (
                  pathname === NotificationCenterRoutes.NOTIFICATIONS_CENTER
                ) {
                  let nextNotifications: NotificationItem[] = [];

                  if (notifications) {
                    nextNotifications = ([] as NotificationItem[]).concat(
                      notifications,
                    );
                  }

                  nextNotifications.unshift(messageContent.notification);

                  dispatch(setNotificationsItems(nextNotifications));
                }

                if (
                  pathname ===
                    TransactionsRoutes.TRANSACTIONS_NOTIFICATIONS_CENTER &&
                  messageContent?.notifications?.category ===
                    NotificationCategoryEnum.TRANSACTIONS
                ) {
                  let nextNotifications: NotificationItem[] = [];

                  if (transactionsNotifications) {
                    nextNotifications = ([] as NotificationItem[]).concat(
                      transactionsNotifications,
                    );
                  }

                  nextNotifications.unshift(messageContent.notification);

                  dispatch(
                    setTransactionsNotificationsItems(nextNotifications),
                  );
                }

                if (
                  pathname === ClientsRoutes.CUSTOMERS_NOTIFICATIONS_CENTER &&
                  (messageContent?.notification?.category ===
                    NotificationCategoryEnum.MESSAGING ||
                    messageContent?.notification?.category ===
                      NotificationCategoryEnum.CUSTOMERS)
                ) {
                  let nextNotifications: NotificationItem[] = [];

                  if (customersNotifications) {
                    nextNotifications = ([] as NotificationItem[]).concat(
                      customersNotifications,
                    );
                  }

                  nextNotifications.unshift(messageContent.notification);

                  dispatch(setCustomersNotificationsItems(nextNotifications));
                }
              } else if (messageData.eventType === 'ON_FILE_READY') {
                const formattedName = messageContent.name.replace('.xlsx', '');
                const titleArray = formattedName.split('-');

                title = titleArray
                  ?.map(
                    (titleItem: string) =>
                      `${titleItem?.charAt(0)?.toUpperCase()}${titleItem
                        ?.slice(1)
                        ?.toLowerCase()}`,
                  )
                  .join(' ');
              } else if (messageData.eventType === 'ON_ACCOUNT_CREATE') {
                title = `Account ${messageContent.fullNumber} ${
                  messageContent.clientName
                    ? `for ${messageContent.clientName}`
                    : ''
                } was created`;
              } else if (messageData.eventType === 'ON_PSP_ACCOUNT_CREATE') {
                title = `Bank account ${messageContent.companyName ? `in ${messageContent.companyName}` : ''} was created`;
              } else {
                title = messageContent.name;
              }
            }

            let exportTitle = '';

            if (messageData.eventType === 'ON_FILE_READY') {
              exportTitle = 'Report ready: ';

              if (title.toLowerCase().includes('transaction')) {
                exportTitle = 'Export ready: ';
              }
            }

            if (browserNotificationEnabled) {
              notify({ text: `${exportTitle}${title}`, id: messageData.id });
            }

            const nextNotifications: NotificationMessage[] = [];

            if (notificationsMessages) {
              notificationsMessages.forEach((item) => {
                if (
                  !nextNotifications?.find(
                    (notification) => notification.id === item.id,
                  )
                ) {
                  nextNotifications.push(item);
                }
              });
            }

            nextNotifications.unshift({
              ...messageData,
              readAt: null,
            });

            dispatch(setNotificationMessages(nextNotifications));

            if (
              messageData.eventType === 'ON_ACCOUNT_CREATE' &&
              messageData.content.organizationId
            ) {
              if (
                messageData.content.organizationType === 'INDIVIDUAL' &&
                pathname.includes(
                  `/customers/individual/${messageData.content.organizationId}`,
                )
              ) {
                getIndividualUser(messageData.content.organizationId);
              }

              if (
                messageData.content.organizationType === 'CORPORATE' &&
                pathname.includes(
                  `/customers/corporate/${messageData.content.organizationId}`,
                )
              ) {
                getCorporateUser(messageData.content.organizationId);
              }
            }

            if (
              messageData.eventType === 'ON_ACCOUNT_CREATE' &&
              !messageData.content.organizationId
            ) {
              if (
                pathname.includes('/accounts/list') &&
                search &&
                search.includes(
                  `categoryId=${messageData.content.categoryId}`,
                ) &&
                search.includes(`groupId=${messageData.content.groupId}`) &&
                search.includes(`primaryId=${messageData.content.primaryId}`)
              ) {
                getAccountsStats();
                getAccountsList(`${search}&take=20`);
              }
            }

            if (
              messageData?.eventType === 'ON_PSP_ACCOUNT_CREATE' &&
              pathname.includes('/bank-and-psp') &&
              !pathname.includes('list')
            ) {
              getPspAccountsList({
                id: bankId as string,
                query: `${search}${search ? '&' : '?'}take=${accountsTakeCount || '20'}`,
              });
            }

            if (notificationVisible?.isVisible) {
              setSocketMessageNotificationVisible({
                type: notificationVisible?.type,
                data: parsed?.data,
                onClose: () => {
                  dispatch(
                    setNotificationVisible({
                      visible: false,
                      text: '',
                      type: '',
                    }),
                  );
                },
              });
            }
          }

          if (
            mainChannels?.find((item) => item.id === messageData?.channelId)
          ) {
            const nextMainChannels = mainChannels.map((item) => {
              const result = { ...item };

              if (result.id === messageData.channelId) {
                result.messages = {
                  edges: [
                    {
                      node: {
                        channelId: messageData.channelId,
                        content: messageData?.content || 'Document uploaded',
                        createdAt: new Date().toISOString(),
                      },
                    },
                  ],
                };

                result.updatedAt = new Date().toISOString();
              }

              return result;
            });

            dispatch(setMainChannels(nextMainChannels));
          } else if (
            requestChannels?.find((item) => item.id === messageData?.channelId)
          ) {
            const nextRequestChannels = requestChannels.map((item) => {
              const result = { ...item };

              if (result.id === messageData.channelId) {
                result.messages = {
                  edges: [
                    {
                      node: {
                        channelId: messageData.channelId,
                        content: messageData?.content || 'Document uploaded',
                        createdAt: new Date().toISOString(),
                      },
                    },
                  ],
                };

                result.updatedAt = new Date().toISOString();
              }

              return result;
            });

            dispatch(setRequestChannels(nextRequestChannels));
          }

          if (messages && !messageData.eventType) {
            let nextChannelMessages = ([] as Message[]).concat(messages);

            const nextMessage: any = {
              channelId: messageData.channelId,
              type: MessageType.TEXT,
              createdAt: new Date().toISOString(),
              content: messageData.content,
              employeeId: messageData?.employeeId,
              id: messageData.id,
              edited: messageData.edited,
              updatedAt: messageData?.updatedAt,
            };

            if (messageData.employeeId) {
              let messageEmployee;

              if (users && users.length) {
                messageEmployee = users?.find(
                  (item) => item.id === messageData?.employeeId,
                );
              }

              if (messageEmployee) {
                nextMessage.employee = messageEmployee;
                nextMessage.readAt = new Date().toISOString();
              }
            }

            const messageWithThisUserId =
              messageData.userId &&
              nextChannelMessages.find(
                (item) => item.userId === messageData.userId,
              );

            if (messageWithThisUserId) {
              nextMessage.user = messageWithThisUserId.user;
            }

            if (messageData.documentId) {
              const document = await getMessageDocumentAction(
                messageData.channelId,
                messageData.documentId,
              );

              if (document && document.data) {
                nextMessage.documentId = messageData.documentId;

                nextMessage.document = {
                  name: document.data.name,
                };

                let nextDocuments: Document[] = [];

                if (documents) {
                  nextDocuments = ([] as Document[]).concat(documents);
                }

                nextDocuments.push(document.data);

                dispatch(setDocuments(nextDocuments));
              }
            }

            if (
              !nextChannelMessages.find((item) => item.id === nextMessage.id)
            ) {
              nextChannelMessages.push(nextMessage);
            } else if (
              nextChannelMessages.find((item) => item.id === nextMessage.id) &&
              parsed?.event === 'edit-message'
            ) {
              nextChannelMessages = nextChannelMessages?.map((item) => {
                let result = { ...item };

                if (result.id === nextMessage.id) {
                  result = nextMessage;
                }

                return result;
              });
            }

            dispatch(setMessages(nextChannelMessages));

            if (scrolled && parsed?.event !== 'edit-message') {
              dispatch(setNewMessageCount(newMessageCount + 1));
            }

            if (parsed?.event !== 'edit-message') {
              setTimeout(() => {
                if (scrollRef && scrollRef.current) {
                  const { scrollHeight } = scrollRef.current;

                  if (scrollHeight && !scrolled) {
                    scrollRef.current.scrollTop = scrollHeight;

                    if (newMessageCount > 0) {
                      dispatch(setNewMessageCount(0));
                    }
                  }
                }
              }, 15);
            }
          }
        }
      },
      retryOnError: true,
      reconnectAttempts: 1000,
      reconnectInterval: () => 3000,
    },
    Boolean(
      accessToken ||
      (accessToken && prevAccessToken && prevAccessToken !== accessToken),
    ),
  );

  const connectionStatus = {
    [ReadyState.CONNECTING]: 'Connecting',
    [ReadyState.OPEN]: 'Open',
    [ReadyState.CLOSING]: 'Closing',
    [ReadyState.CLOSED]: 'Closed',
    [ReadyState.UNINSTANTIATED]: 'Uninstantiated',
  }[readyState];

  const prevConnectionStatus = usePrevious(connectionStatus);

  const userId = localStorage.getItem('userId');

  useIsAuthenticated({
    permissions: activePermissions,
  });

  useEffect(() => {
    if (currencies) {
      dispatch(setCustomersCurrencies(currencies));
      dispatch(setConfigurationCurrencies(currencies));
      dispatch(setTransactionsCurrencies(currencies));
      dispatch(setAdministrationCurrencies(currencies));
      dispatch(setCurrenciesExchangeCurrencies(currencies));
      dispatch(setAmlCurrencies(currencies));
      dispatch(setReportsCurrencies(currencies));
    }

    if (countries) {
      dispatch(setCustomersCountries(countries));
      dispatch(setConfigurationCountries(countries));
      dispatch(setTransactionsCountries(countries));
      dispatch(setAdministrationCountries(countries));
    }

    if (baseCurrency) {
      dispatch(setCustomersBaseCurrency(baseCurrency));
      dispatch(setConfigurationBaseCurrency(baseCurrency));
      dispatch(setTransactionsBaseCurrency(baseCurrency));
      dispatch(setAmlBaseCurrency(baseCurrency));
      dispatch(setReportsBaseCurrency(baseCurrency));
    }

    if (settings) {
      dispatch(setAdministationPspDetails(settings));
    }
  }, [currencies, countries, baseCurrency, settings]);

  useEffect(() => {
    if (userIdStore && 'Notification' in window) {
      if (Notification.permission === 'default') {
        Notification.requestPermission().then((permission) => {
          if (permission === 'granted') {
            dispatch(setBrowserNotificationEnabled(true));

            const browserNotificationEnabledJSON = localStorage.getItem(
              'browserNotificationEnabled',
            );

            let nextBrowserNotificationEnabled;

            if (browserNotificationEnabledJSON) {
              const parsed = JSON.parse(browserNotificationEnabledJSON);

              if (parsed && Array.isArray(parsed)) {
                nextBrowserNotificationEnabled = parsed?.map((item) => {
                  const result = { ...item };

                  if (item.userId === userId) {
                    result.browserNotificationEnabled = true;
                  }

                  return result;
                });
              } else {
                nextBrowserNotificationEnabled = [
                  { browserNotificationEnabled: true, userId },
                ];
              }
            } else {
              nextBrowserNotificationEnabled = [
                { browserNotificationEnabled: true, userId },
              ];
            }

            localStorage.setItem(
              'browserNotificationEnabled',
              JSON.stringify(nextBrowserNotificationEnabled),
            );
          }
        });
      }
    }
  }, [userIdStore]);

  useEffect(() => {
    const browserNotificationEnabledJSON = localStorage.getItem(
      'browserNotificationEnabled',
    );

    if (browserNotificationEnabledJSON) {
      const parsed = JSON.parse(browserNotificationEnabledJSON);

      if (userIdStore && 'Notification' in window) {
        if (parsed) {
          let currentUserNotification;
          let nextBrowserNotificationEnabled: {
            browserNotificationEnabled: boolean;
            userId?: string;
          }[] = [];

          if (Array.isArray(parsed)) {
            currentUserNotification = parsed?.find(
              (item) => item.userId === userIdStore,
            );
          } else {
            currentUserNotification = parsed;
          }

          if (Notification.permission === 'granted') {
            dispatch(
              setBrowserNotificationEnabled(
                currentUserNotification?.browserNotificationEnabled,
              ),
            );

            if (
              Array.isArray(parsed) &&
              !parsed.find((item) => item.userId === userIdStore)
            ) {
              nextBrowserNotificationEnabled = parsed;

              nextBrowserNotificationEnabled.push({
                browserNotificationEnabled: true,
                userId: userIdStore,
              });

              dispatch(setBrowserNotificationEnabled(true));

              localStorage.setItem(
                'browserNotificationEnabled',
                JSON.stringify(nextBrowserNotificationEnabled),
              );
            }
          } else if (
            Notification.permission === 'denied' &&
            currentUserNotification.browserNotificationEnabled
          ) {
            dispatch(setBrowserNotificationEnabled(false));

            if (parsed && Array.isArray(parsed)) {
              if (parsed?.find((item) => item.id === userIdStore)) {
                nextBrowserNotificationEnabled = parsed?.map((item) => {
                  const result = { ...item };

                  if (item.userId === userIdStore) {
                    result.browserNotificationEnabled = false;
                  }

                  return result;
                });
              } else {
                nextBrowserNotificationEnabled.push({
                  browserNotificationEnabled: false,
                  userId: userIdStore,
                });
              }
            } else {
              nextBrowserNotificationEnabled = [
                { browserNotificationEnabled: false, userId: userIdStore },
              ];
            }

            localStorage.setItem(
              'browserNotificationEnabled',
              JSON.stringify(nextBrowserNotificationEnabled),
            );
          }
        }
      }
    } else if (
      !browserNotificationEnabledJSON &&
      'Notification' in window &&
      Notification.permission === 'granted'
    ) {
      localStorage.setItem(
        'browserNotificationEnabled',
        JSON.stringify([
          { browserNotificationEnabled: true, userId: userIdStore },
        ]),
      );
    }
  }, [userIdStore]);

  useEffect(() => {
    if (pspDetails) {
      dispatch(
        setCustomersSettings(
          pspDetails as { key: string; data: { value: boolean } }[],
        ),
      );

      dispatch(
        setAccountsSettings(
          pspDetails as { key: string; data: { value: boolean } }[],
        ),
      );

      dispatch(
        setTransactionsSettings(
          pspDetails as { key: string; data: { value: boolean } }[],
        ),
      );

      dispatch(
        setFintracSettings(
          pspDetails as { key: string; data: { value: boolean } }[],
        ),
      );

      dispatch(
        setConfigurationSettings(
          pspDetails as { key: string; data: { value: boolean } }[],
        ),
      );

      dispatch(
        setReportsSettings(
          pspDetails as { key: string; data: { value: string } }[],
        ),
      );

      const cardsEnabled = pspDetails?.find(
        (item) => item?.key === PSPDetailsKeys.CARDS,
      );

      dispatch(setCardsEnabled(cardsEnabled?.data?.value as boolean));

      dispatch(setRootSettings(pspDetails as PSPDetails[]));
    }
  }, [pspDetails]);

  useEffect(() => {
    if (exchangeRateSource || settingsRateSource || customersRateSource) {
      if (!transactionsRateSource) {
        dispatch(
          setTransactionsRateSources(
            (exchangeRateSource as {
              name: string;
              description: string;
              id: string;
            }[]) ||
              (settingsRateSource as {
                name: string;
                description: string;
                id: string;
              }[]),
          ),
        );
      }

      if (!customersRateSource && (exchangeRateSource || settingsRateSource)) {
        dispatch(
          setCustomersRateSource(
            (exchangeRateSource as RateSource[]) ||
              (settingsRateSource as RateSource[]),
          ),
        );
      }

      if (!settingsRateSource && (exchangeRateSource || customersRateSource)) {
        dispatch(
          setAdministationRateSources(
            (exchangeRateSource as RateSource[]) ||
              (customersRateSource as RateSource[]),
          ),
        );
      }

      if (!exchangeRateSource && (settingsRateSource || customersRateSource)) {
        dispatch(
          setCurrenciesExchangeRateSources(
            (settingsRateSource as RateSource[]) ||
              (customersRateSource as RateSource[]),
          ),
        );
      }
    }
  }, [exchangeRateSource, settingsRateSource, transactionsRateSource]);

  useEffect(() => {
    if (
      !users &&
      activePermissions &&
      activePermissions.includes(PermissionEnum.USERS_LIST)
    ) {
      getUsers('');
    }
  }, [users, activePermissions]);

  useEffect(() => {
    if (paymentTypes) {
      dispatch(setClientsPaymentTypes(paymentTypes));
      dispatch(setTransactionsPaymentTypes(paymentTypes));
      dispatch(setConfigurationPaymentTypes(paymentTypes));
      dispatch(setEmployeesPaymentTypes(paymentTypes));
      dispatch(setAMLPaymentTypes(paymentTypes));
    }
  }, [paymentTypes]);

  useEffect(() => {
    if (activePermissions) {
      dispatch(setClientsActivePermissions(activePermissions));
      dispatch(setTransactionsActivePermissions(activePermissions));
      dispatch(setAmlActivePermissions(activePermissions));
      dispatch(setConfigurationActivePermissions(activePermissions));
      dispatch(setEmployeesActivePermissions(activePermissions));
      dispatch(setReportsActivePermissions(activePermissions));
      dispatch(setMessagingActivePermissions(activePermissions));
      dispatch(setCurrensiesExchangeActivePermissions(activePermissions));
      dispatch(setDashboardActivePermissions(activePermissions));
      dispatch(setNotificationCenterPermissions(activePermissions));
    }
  }, [activePermissions, dispatch]);

  useEffect(() => {
    if (
      prevPaymentTypes &&
      settingsPaymentTypes &&
      !isEqual(settingsPaymentTypes, prevPaymentTypes) &&
      isSuccess
    ) {
      dispatch(setRootPaymentTypes(settingsPaymentTypes));
    }
  }, [settingsPaymentTypes, prevPaymentTypes, isSuccess]);

  useEffect(() => {
    const companyName = localStorage.getItem('companyName');
    const faviconStorage = localStorage.getItem('favicon');

    if (companyName) {
      document.title = `${companyName}: Ultimate core banking software`;
    }

    if (faviconStorage) {
      const favicon = document.getElementById('favicon');

      if (favicon) {
        // @ts-ignore
        favicon.href = faviconStorage;
      }
    }

    if (whitelabel) {
      localStorage.setItem('companyName', whitelabel?.data?.value?.companyName);
      localStorage.setItem('favicon', whitelabel?.data?.value?.favicon);

      document.title = `${whitelabel?.data?.value?.companyName}: Ultimate core banking software`;

      const favicon = document.getElementById('favicon');

      if (favicon) {
        // @ts-ignore
        favicon.href = whitelabel?.data?.value?.favicon;
      }
    }
  }, [whitelabel]);

  useEffect(() => {
    if (userIdStore) {
      getPaymentTypes();
      getRootDitionaries();
      getTransactionTypes();
      getFFSettings();

      if (!roles) {
        getRoles('');
      }
    }
  }, [userIdStore, roles]);

  const elements = useRoutes([
    { ...authRoutes },
    {
      path: '/',
      element: usePrivateRoutes({
        redirectLogin: '/login',
        userId: JSON.parse(userId as string)?.userId,
        children: (
          <Layout
            sendMessage={sendMessage}
            reSubscribe={Boolean(
              accessToken && prevAccessToken && prevAccessToken !== accessToken,
            )}
            notificationPanelVisible={notificationPanelVisible}
            setNotificationPanelVisible={setNotificationPanelVisible}
            selectedNotificationId={selectedNotificationId}
            setSelectedNotificationId={setSelectedNotificationId}
          >
            <Outlet
              context={{
                connectionStatus: prevConnectionStatus,
                sendMessage,
                messagesScrollRef: scrollRef,
              }}
            />
          </Layout>
        ),
      }),
      children: [
        { ...dashboardRoutes },
        { ...accountsRoutes },
        { ...customerRoutes },
        { ...transactionsRoutes },
        ...amlKycRoutes,
        ...configurationRoutes,
        ...administrationRoutes,
        { ...reportsRoutes },
        { ...messagesRoutes },
        { ...exchangeRoutes },
        { ...notificationCenterRoutes },
      ],
    },
  ]);

  return elements;
};

export default RootRoutes;
